import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, Bug, Sparkles, Heart, MoreHorizontal, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '@/lib/api';

const CATEGORIES = [
  { id: 'bug',        label: 'Bug',         icon: Bug,             color: 'text-red-600 bg-red-50 border-red-200' },
  { id: 'idea',       label: 'Idea',        icon: Sparkles,        color: 'text-teal-700 bg-teal-50 border-teal-200' },
  { id: 'compliment', label: 'Compliment',  icon: Heart,           color: 'text-pink-600 bg-pink-50 border-pink-200' },
  { id: 'other',      label: 'Other',       icon: MoreHorizontal,  color: 'text-stone-600 bg-stone-50 border-stone-200' },
];

const STATUS_LABEL = {
  open: { label: 'Open', cls: 'bg-amber-50 text-amber-700' },
  in_progress: { label: 'In progress', cls: 'bg-blue-50 text-blue-700' },
  closed: { label: 'Resolved', cls: 'bg-teal-50 text-teal-700' },
};

function fmtDate(iso) {
  if (!iso) return '';
  try { return format(parseISO(iso), 'MMM d, h:mm a'); } catch { return iso; }
}

export default function FeedbackPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [category, setCategory] = useState('idea');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    loadMine();
  }, [user, loading, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMine = async () => {
    setLoadingMine(true);
    try {
      const { data } = await axios.get(`${API_BASE}/feedback/mine`, { withCredentials: true });
      setMine(data || []);
    } catch {
      /* non-critical */
    } finally {
      setLoadingMine(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (message.trim().length < 3) {
      toast.error('Please write a bit more.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/feedback`,
        {
          category,
          message: message.trim(),
          page_url: location.state?.fromPath || document.referrer || '',
        },
        { withCredentials: true },
      );
      toast.success('Thanks — we read every message.');
      setMessage('');
      setCategory('idea');
      loadMine();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send — try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20" data-testid="feedback-page">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-semibold text-stone-900">Send feedback</h1>
        <p className="text-sm text-stone-500 mt-1 mb-6">
          Found a bug? Got an idea? Want to say something nice? We&apos;re listening.
        </p>

        <Card className="p-5 rounded-2xl border-stone-200 bg-white mb-8" data-testid="feedback-form">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="text-sm font-medium text-stone-700 mb-2">What kind of feedback?</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {CATEGORIES.map((c) => {
                  const active = c.id === category;
                  const Icon = c.icon;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCategory(c.id)}
                      className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition ${
                        active ? c.color + ' ring-2 ring-offset-1 ring-teal-300' : 'border-stone-200 text-stone-500 hover:border-stone-300'
                      }`}
                      data-testid={`category-${c.id}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs font-medium">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  category === 'bug'
                    ? 'What went wrong? What were you doing when it happened?'
                    : category === 'idea'
                    ? 'What would you love Kinsy to do?'
                    : category === 'compliment'
                    ? 'We&apos;d love to hear it 🤍'
                    : 'Tell us anything…'
                }
                className="min-h-[140px] rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                maxLength={5000}
                data-testid="feedback-message"
              />
              <div className="text-xs text-stone-400 mt-1 text-right">{message.length} / 5000</div>
            </div>

            <Button
              type="submit"
              disabled={submitting || message.trim().length < 3}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              data-testid="submit-feedback"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send feedback
            </Button>
          </form>
        </Card>

        {/* My past feedback */}
        <h2 className="text-lg font-semibold text-stone-900 mb-3">Your feedback history</h2>
        {loadingMine ? (
          <div className="text-sm text-stone-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div>
        ) : mine.length === 0 ? (
          <p className="text-sm text-stone-500">No past feedback yet.</p>
        ) : (
          <div className="space-y-3" data-testid="my-feedback-list">
            {mine.map((f) => {
              const cat = CATEGORIES.find((c) => c.id === f.category) || CATEGORIES[3];
              const stat = STATUS_LABEL[f.status] || STATUS_LABEL.open;
              const Icon = cat.icon;
              return (
                <Card key={f.id} className="p-4 rounded-2xl border-stone-200 bg-white">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-stone-500" />
                      <span className="text-sm font-medium text-stone-700">{cat.label}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="secondary" className={`text-xs ${stat.cls}`}>{stat.label}</Badge>
                      <span className="text-xs text-stone-400">{fmtDate(f.created_at)}</span>
                    </div>
                  </div>
                  <p className="text-sm text-stone-700 whitespace-pre-wrap">{f.message}</p>
                  {f.admin_reply && (
                    <div className="mt-3 p-3 rounded-xl bg-teal-50/60 border border-teal-100">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="w-4 h-4 text-teal-700" />
                        <span className="text-xs font-semibold text-teal-800">Kinsy replied</span>
                        {f.replied_at && <span className="text-xs text-teal-600/80">· {fmtDate(f.replied_at)}</span>}
                      </div>
                      <p className="text-sm text-teal-900 whitespace-pre-wrap">{f.admin_reply}</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <MobileNav />
    </div>
  );
}
